import React, { useState } from "react";

/** A string identifying the languages the wiki is available in */
export type Theme = (typeof THEMES)[number];

/** List of all {@link Theme}s */
export const THEMES = ["system", "dark", "light"] as const;

/** Data provided by the {@link THEME_CONTEXT} */
export type ThemeContext = {
    /** The theme */
    theme: Theme;
    /** Callback to set a new theme */
    setTheme: (theme: Theme) => void;
};

/** {@link React.Context} to access {@link Theme} */
const THEME_CONTEXT = React.createContext<ThemeContext>({
    theme: "system",

    /** Set a new theme */
    setTheme: () => {},
});
THEME_CONTEXT.displayName = "Theme";
export default THEME_CONTEXT;

/**
 * The properties for {@link ThemeProvider}
 */
export type ThemeProviderProps = {
    /**
     * Children of the theme provider
     */
    children: React.ReactNode;
};

/**
 * The provider of a theme context
 */
export function ThemeProvider(props: ThemeProviderProps) {
    const localTheme = localStorage.getItem("theme");
    const [theme, setTheme] = useState<Theme>((localTheme as Theme) ?? "system");

    const setNewTheme = (newTheme: Theme) => {
        if (newTheme === "system") {
            localStorage.removeItem("theme");
        } else if (newTheme === "light") {
            localStorage.setItem("theme", "light");
        } else {
            localStorage.setItem("theme", "dark");
        }

        if (
            localStorage.theme === "dark" ||
            (!("theme" in localStorage) && window.matchMedia("(prefers-color-scheme: dark)").matches)
        ) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
        setTheme(newTheme);
    };

    return (
        <THEME_CONTEXT.Provider
            value={{
                theme,
                setTheme: setNewTheme,
            }}
        >
            {props.children}
        </THEME_CONTEXT.Provider>
    );
}
